import { useMemo, useState } from 'react';
import {
  IconAlert,
  IconCalendar,
  IconCheck,
  IconClock,
  IconPlus,
  IconSearch,
  IconWrench,
} from '../components/icons';
import { RowActions } from '../components/RowActions';
import { Badge, Card, EmptyState, ErrorMessage, Field, Loading, Modal, ModuleHeader, SearchSelect, StatCard, Tabs, useAsync, useToast } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatDuration } from '../lib/format';

type View = 'kacheln' | 'liste';
type GroupBy = 'keine' | 'objekt' | 'kategorie' | 'status';
type SortKey = 'due' | 'name' | 'property' | 'category' | 'interval';

/**
 * Zustand eines Plans gegenüber seinem eigenen Vorlauf.
 *
 * Nicht gegen eine feste Zwei-Wochen-Grenze: Ein Aufzug mit 21 Tagen Vorlauf
 * ist früher „demnächst“ als ein Rauchmelder mit sieben. Wer beide gegen
 * dieselbe Grenze prüft, meldet den einen zu spät und den anderen zu früh.
 */
function dueState(plan: any): 'due' | 'soon' | 'planned' {
  if (!plan.next_due_date) return 'planned';
  const days = Math.ceil((new Date(plan.next_due_date).getTime() - Date.now()) / 86_400_000);
  if (days <= 0) return 'due';
  return days <= (plan.lead_days ?? 14) ? 'soon' : 'planned';
}

const STATE_LABELS: Record<string, string> = {
  due: 'Fällig',
  soon: 'Demnächst',
  planned: 'Geplant',
};

export function MaintenancePage() {
  const { can } = useAuth();
  const { notify } = useToast();
  const [editing, setEditing] = useState<any | null>(null);
  const [view, setView] = useState<View>('liste');
  const [groupBy, setGroupBy] = useState<GroupBy>('objekt');
  const [sort, setSort] = useState<SortKey>('due');
  const [filters, setFilters] = useState({
    search: '',
    propertyId: '',
    maintenanceCategoryId: '',
    assigneeId: '',
    due: '',
    state: 'active',
  });

  const plans = useAsync(() => api.get('/api/maintenance', filters), [JSON.stringify(filters)]);
  const properties = useAsync(() => api.get('/api/properties').catch(() => []), []);
  const categories = useAsync(() => api.get('/api/catalogs/maintenance-categories').catch(() => []), []);
  const users = useAsync(() => api.get('/api/users').catch(() => []), []);

  const mayManage = can('maintenance.manage');
  const setFilter = (key: keyof typeof filters, value: string) =>
    setFilters((current) => ({ ...current, [key]: value }));

  const runNow = async () => {
    const result = await api.post('/api/maintenance/run');
    notify(`${result.created} Aufträge erzeugt, ${result.breaches} SLA-Verletzung(en) erkannt.`, 'success');
    plans.reload();
  };

  const list: any[] = plans.data ?? [];

  const sorted = useMemo(() => {
    const copy = [...list];
    const compare: Record<SortKey, (a: any, b: any) => number> = {
      due: (a, b) => String(a.next_due_date ?? '').localeCompare(String(b.next_due_date ?? '')),
      name: (a, b) => String(a.name).localeCompare(String(b.name), 'de'),
      property: (a, b) => String(a.property_name ?? '').localeCompare(String(b.property_name ?? ''), 'de'),
      category: (a, b) =>
        String(a.maintenance_category_name ?? '').localeCompare(String(b.maintenance_category_name ?? ''), 'de'),
      interval: (a, b) => intervalDays(a) - intervalDays(b),
    };
    return copy.sort(compare[sort]);
  }, [list, sort]);

  /**
   * Gruppierung.
   *
   * Nach Objekt fragt, wer eine Liegenschaft begeht; nach Kategorie fragt, wer
   * eine Prüfpflicht nachweisen muss. Beide Fragen kommen im Betrieb vor, und
   * keine ist die richtigere – deshalb wählbar statt festgelegt.
   */
  const groups = useMemo(() => {
    if (groupBy === 'keine') return [{ key: 'alle', label: '', plans: sorted }];
    const map = new Map<string, { key: string; label: string; plans: any[] }>();
    for (const plan of sorted) {
      const [key, label] =
        groupBy === 'objekt'
          ? [String(plan.property_id ?? 'ohne'), plan.property_name ?? 'Ohne Objektzuordnung']
          : groupBy === 'kategorie'
            ? [
                String(plan.maintenance_category_id ?? 'ohne'),
                plan.maintenance_category_name ?? 'Ohne Wartungskategorie',
              ]
            : [dueState(plan), STATE_LABELS[dueState(plan)]];
      if (!map.has(key)) map.set(key, { key, label, plans: [] });
      map.get(key)!.plans.push(plan);
    }
    // Beim Status die fachliche Reihenfolge erzwingen, sonst sortiert die
    // Einfügereihenfolge – und die hängt am Zufall der Daten.
    const entries = [...map.values()];
    if (groupBy === 'status') {
      const order = ['due', 'soon', 'planned'];
      entries.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
    } else {
      entries.sort((a, b) => a.label.localeCompare(b.label, 'de'));
    }
    return entries;
  }, [sorted, groupBy]);

  const remove = async (plan: any, permanent: boolean) => {
    const question = permanent
      ? `Wartungsplan „${plan.name}“ endgültig löschen? Bereits erzeugte Aufträge bleiben bestehen.`
      : `Wartungsplan „${plan.name}“ aussetzen? Er erzeugt dann keine Aufträge mehr, bleibt aber als Nachweis stehen.`;
    if (!window.confirm(question)) return;
    try {
      await api.delete(`/api/maintenance/${plan.id}`, permanent ? { permanent: 'true' } : undefined);
      notify(permanent ? 'Wartungsplan gelöscht.' : 'Wartungsplan ausgesetzt.', 'success');
      plans.reload();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Aktion nicht möglich.', 'error');
    }
  };

  const activeFilters = Object.entries(filters).filter(
    ([key, value]) => value && !(key === 'state' && value === 'active'),
  );

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconCalendar width={22} height={22} />}
        title="Wartungspläne"
        subtitle={
          <>
            Präventive und wiederkehrende Prüfungen. Aus jedem Plan werden – abhängig vom Vorlauf – automatisch
            Aufträge erzeugt und der nächste Fälligkeitstermin fortgeschrieben.
          </>
        }
        pill="Prävention"
        tone="warning"
        actions={
          mayManage && (
            <>
              <button className="btn" onClick={runNow}>
                Jetzt erzeugen
              </button>
              <button className="btn primary" onClick={() => setEditing({ isNew: true })}>
                <IconPlus width={15} height={15} /> Wartungsplan anlegen
              </button>
            </>
          )
        }
      />

      {plans.loading && !plans.data ? (
        <Loading />
      ) : (
        <>
          <div className="grid cols-4">
            <StatCard
              label="Pläne"
              value={list.length}
              tone="primary"
              icon={<IconWrench width={17} height={17} />}
              hint={filters.state === 'active' ? 'aktiv im Zugriff' : 'in der Auswahl'}
            />
            <StatCard
              label="Fällig"
              value={list.filter((plan) => dueState(plan) === 'due').length}
              tone={list.some((plan) => dueState(plan) === 'due') ? 'danger' : 'success'}
              emphasis={list.some((plan) => dueState(plan) === 'due') ? 'attention' : undefined}
              icon={<IconAlert width={17} height={17} />}
              hint="Termin erreicht oder überschritten"
            />
            <StatCard
              label="Demnächst"
              value={list.filter((plan) => dueState(plan) === 'soon').length}
              tone="warning"
              icon={<IconClock width={17} height={17} />}
              hint="innerhalb des jeweiligen Vorlaufs"
            />
            <StatCard
              label="Ausgesetzt"
              value={list.filter((plan) => !plan.is_active).length}
              tone="info"
              icon={<IconCheck width={17} height={17} />}
              hint="erzeugen derzeit keine Aufträge"
            />
          </div>

          {/* ---------------------------- Werkzeugleiste ------------------- */}
          <Card noBody>
            <div className="card-body tight">
              <div className="row" style={{ gap: 10 }}>
                <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 340 }}>
                  <input
                    type="search"
                    placeholder="Plan, Beschreibung oder Objekt suchen …"
                    value={filters.search}
                    onChange={(event) => setFilter('search', event.target.value)}
                    style={{ paddingLeft: 34 }}
                  />
                  <span style={{ position: 'absolute', left: 11, top: 10, color: 'var(--text-subtle)' }}>
                    <IconSearch width={15} height={15} />
                  </span>
                </div>

                <label className="toolbar-field">
                  <span>Objekt</span>
                  <SearchSelect
                    value={String(filters.propertyId ?? '')}
                    onChange={(wert) => setFilter('propertyId', wert)}
                    options={(properties.data ?? []).map((property: any) => ({ value: String(property.id), label: String(property.name) }))}
                    placeholder="alle"
                  />
                </label>

                <label className="toolbar-field">
                  <span>Kategorie</span>
                  <SearchSelect
                    value={String(filters.maintenanceCategoryId ?? '')}
                    onChange={(wert) => setFilter('maintenanceCategoryId', wert)}
                    options={(categories.data ?? []).map((category: any) => ({ value: String(category.id), label: String(category.name) }))}
                    placeholder="alle"
                  />
                </label>

                <label className="toolbar-field">
                  <span>Zuständig</span>
                  <SearchSelect
                    value={String(filters.assigneeId ?? '')}
                    onChange={(wert) => setFilter('assigneeId', wert)}
                    options={(users.data?.items ?? users.data ?? []).map((person: any) => ({ value: String(person.id), label: `${person.first_name} ${person.last_name}` }))}
                    placeholder="alle"
                  />
                </label>

                <label className="toolbar-field">
                  <span>Termin</span>
                  <select value={filters.due} onChange={(event) => setFilter('due', event.target.value)}>
                    <option value="">alle</option>
                    <option value="due">fällig</option>
                    <option value="soon">demnächst</option>
                  </select>
                </label>

                <label className="toolbar-field">
                  <span>Status</span>
                  <select value={filters.state} onChange={(event) => setFilter('state', event.target.value)}>
                    <option value="active">aktiv</option>
                    <option value="paused">ausgesetzt</option>
                    <option value="">alle</option>
                  </select>
                </label>
              </div>

              <div className="row between" style={{ marginTop: 10 }}>
                <div className="row" style={{ gap: 10 }}>
                  <label className="toolbar-field">
                    <span>Gruppierung</span>
                    <select value={groupBy} onChange={(event) => setGroupBy(event.target.value as GroupBy)}>
                      <option value="keine">ohne</option>
                      <option value="objekt">nach Objekt</option>
                      <option value="kategorie">nach Kategorie</option>
                      <option value="status">nach Termin</option>
                    </select>
                  </label>
                  <label className="toolbar-field">
                    <span>Sortierung</span>
                    <select value={sort} onChange={(event) => setSort(event.target.value as SortKey)}>
                      <option value="due">nächste Fälligkeit</option>
                      <option value="name">Bezeichnung</option>
                      <option value="property">Objekt</option>
                      <option value="category">Kategorie</option>
                      <option value="interval">Intervall</option>
                    </select>
                  </label>
                  {activeFilters.length > 0 && (
                    <button
                      className="btn sm ghost"
                      onClick={() =>
                        setFilters({
                          search: '',
                          propertyId: '',
                          maintenanceCategoryId: '',
                          assigneeId: '',
                          due: '',
                          state: 'active',
                        })
                      }
                    >
                      Filter zurücksetzen ({activeFilters.length})
                    </button>
                  )}
                </div>

                <Tabs
                  active={view}
                  onChange={(value) => setView(value as View)}
                  tabs={[
                    { key: 'kacheln', label: 'Kacheln' },
                    { key: 'liste', label: 'Liste' },
                  ]}
                />
              </div>
            </div>
          </Card>

          {list.length === 0 ? (
            <Card>
              <EmptyState
                icon={<IconCalendar width={20} height={20} />}
                title="Kein Wartungsplan gefunden"
                hint={
                  activeFilters.length > 0
                    ? 'Für die gewählten Filter gibt es keinen Treffer.'
                    : 'Legen Sie einen Plan an – daraus entstehen die wiederkehrenden Aufträge von selbst.'
                }
              />
            </Card>
          ) : (
            groups.map((group) => (
              <div key={group.key} className="stack" style={{ gap: 10 }}>
                {group.label && (
                  <div className="row between">
                    <span className="section-title">{group.label}</span>
                    <span className="small subtle">{group.plans.length} Plan/Pläne</span>
                  </div>
                )}

                {view === 'kacheln' ? (
                  <div className="grid cols-2">
                    {group.plans.map((plan: any) => (
                      <PlanCard
                        key={plan.id}
                        plan={plan}
                        mayManage={mayManage}
                        onEdit={() => setEditing(plan)}
                        onRemove={remove}
                      />
                    ))}
                  </div>
                ) : (
                  <Card noBody>
                    <div className="table-wrap">
                      <table className="data">
                        <thead>
                          <tr>
                            <th>Plan</th>
                            <th>Objekt</th>
                            <th>Kategorie</th>
                            <th>Intervall</th>
                            <th>Nächste Fälligkeit</th>
                            <th>Zuständig</th>
                            <th>Status</th>
                            {mayManage && <th className="actions">Aktionen</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {group.plans.map((plan: any) => {
                            const state = dueState(plan);
                            return (
                              <tr key={plan.id} style={plan.is_active ? undefined : { opacity: 0.55 }}>
                                <td>
                                  <strong>{plan.name}</strong>
                                  {plan.checklist_name && (
                                    <div className="small subtle">Checkliste: {plan.checklist_name}</div>
                                  )}
                                </td>
                                <td className="small">{plan.property_name ?? '–'}</td>
                                <td className="small">
                                  {plan.maintenance_category_name ? (
                                    <Badge color={plan.maintenance_category_color}>
                                      {plan.maintenance_category_name}
                                    </Badge>
                                  ) : (
                                    '–'
                                  )}
                                </td>
                                <td className="small subtle">
                                  alle {plan.interval_value} {intervalLabel(plan.interval_unit, plan.interval_value)}
                                </td>
                                <td className="small">
                                  <strong style={state === 'due' ? { color: 'var(--danger-text)' } : undefined}>
                                    {formatDate(plan.next_due_date)}
                                  </strong>
                                </td>
                                <td className="small subtle">
                                  {plan.first_name ? `${plan.first_name} ${plan.last_name}` : '–'}
                                </td>
                                <td>
                                  {!plan.is_active ? (
                                    <Badge>Ausgesetzt</Badge>
                                  ) : (
                                    <Badge variant={state === 'due' ? 'danger' : state === 'soon' ? 'warning' : ''}>
                                      {STATE_LABELS[state]}
                                    </Badge>
                                  )}
                                </td>
                                {mayManage && (
                                  <td className="actions">
                                    <RowActions
                                      label={plan.name}
                                      onEdit={() => setEditing(plan)}
                                      deletePath={`/api/maintenance/${plan.id}${plan.is_active ? '' : '?permanent=true'}`}
                                      confirmText={
                                        plan.is_active
                                          ? `Wartungsplan „${plan.name}“ aussetzen? Er erzeugt dann keine Aufträge mehr, bleibt aber als Nachweis stehen.`
                                          : `Wartungsplan „${plan.name}“ endgültig löschen? Bereits erzeugte Aufträge bleiben bestehen.`
                                      }
                                      onDeleted={() => plans.reload()}
                                    />
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}
              </div>
            ))
          )}
        </>
      )}

      {editing && (
        <PlanModal
          plan={editing.isNew ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            plans.reload();
            notify('Wartungsplan gespeichert.', 'success');
          }}
        />
      )}
    </div>
  );
}

function PlanCard({
  plan,
  mayManage,
  onEdit,
  onRemove,
}: {
  plan: any;
  mayManage: boolean;
  onEdit: () => void;
  onRemove: (plan: any, permanent: boolean) => void;
}) {
  const state = dueState(plan);
  return (
    <div
      className={`order-card ${
        !plan.is_active ? '' : state === 'due' ? 'tone-danger' : state === 'soon' ? 'tone-warning' : 'tone-primary'
      }`}
      style={plan.is_active ? undefined : { opacity: 0.62 }}
    >
      <div className="row between">
        <span className="row" style={{ gap: 6 }}>
          {plan.maintenance_category_name ? (
            <Badge color={plan.maintenance_category_color}>{plan.maintenance_category_name}</Badge>
          ) : (
            <span className="chip tiny">Ohne Kategorie</span>
          )}
          <span className="small subtle">
            alle {plan.interval_value} {intervalLabel(plan.interval_unit, plan.interval_value)}
          </span>
        </span>
        {plan.is_active && state === 'due' && <span className="badge solid danger">Dringend fällig</span>}
        {plan.is_active && state === 'soon' && <span className="badge solid warning">Demnächst</span>}
        {!plan.is_active && <Badge>Ausgesetzt</Badge>}
      </div>

      <div className="order-card-title">{plan.name}</div>
      <div className="small subtle">{plan.property_name ?? 'Ohne Objektzuordnung'}</div>

      {plan.maintenance_legal_basis && (
        <div className="small subtle" style={{ marginTop: 4 }}>
          Grundlage: {plan.maintenance_legal_basis}
        </div>
      )}

      {plan.checklist_name && (
        <div className="note-box tone-info" style={{ marginTop: 12 }}>
          <IconWrench width={15} height={15} />
          <span>
            <strong>Checkliste:</strong> {plan.checklist_name}
          </span>
        </div>
      )}

      <div className="row between" style={{ marginTop: 14 }}>
        <span className="small">
          <IconClock width={13} height={13} /> Fällig am{' '}
          <strong style={{ color: plan.is_active && state === 'due' ? 'var(--danger-text)' : undefined }}>
            {formatDate(plan.next_due_date)}
          </strong>
        </span>
        <span className="small subtle">
          {plan.first_name ? `${plan.first_name} ${plan.last_name}` : 'ohne Zuweisung'} ·{' '}
          {formatDuration(plan.estimated_minutes)}
        </span>
      </div>

      {mayManage && (
        <div className="row" style={{ marginTop: 12 }}>
          <button className="btn sm" onClick={onEdit}>
            Bearbeiten
          </button>
          <span className="spacer" />
          <button className="btn sm ghost" onClick={() => onRemove(plan, !plan.is_active)}>
            {plan.is_active ? 'Aussetzen' : 'Endgültig löschen'}
          </button>
        </div>
      )}
    </div>
  );
}

function PlanModal({ plan, onClose, onSaved }: { plan: any | null; onClose: () => void; onSaved: () => void }) {
  const catalogs = useAsync(() => api.get('/api/catalogs'), []);
  const properties = useAsync(() => api.get('/api/properties'), []);
  const users = useAsync(() => api.get('/api/users').catch(() => []), []);

  const [form, setForm] = useState({
    name: plan?.name ?? '',
    description: plan?.description ?? '',
    propertyId: plan?.property_id ? String(plan.property_id) : '',
    categoryId: plan?.category_id ? String(plan.category_id) : '',
    maintenanceCategoryId: plan?.maintenance_category_id ? String(plan.maintenance_category_id) : '',
    criticalityId: plan?.criticality_id ? String(plan.criticality_id) : '',
    assigneeId: plan?.assignee_id ? String(plan.assignee_id) : '',
    checklistTemplateId: plan?.checklist_template_id ? String(plan.checklist_template_id) : '',
    intervalUnit: plan?.interval_unit ?? 'month',
    intervalValue: String(plan?.interval_value ?? 1),
    leadDays: String(plan?.lead_days ?? 7),
    estimatedMinutes: String(plan?.estimated_minutes ?? 120),
    nextDueDate: plan?.next_due_date?.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
    isActive: plan ? !!plan.is_active : true,
  });
  const [error, setError] = useState<unknown>(null);
  const set = (key: keyof typeof form, value: any) => setForm((current) => ({ ...current, [key]: value }));

  const maintenanceCategories = catalogs.data?.maintenanceCategories ?? [];

  /**
   * Kategorie gewählt: Intervall und Vorlauf übernehmen.
   *
   * Nur beim Anlegen und nur, solange die Felder unberührt sind – bei einem
   * bestehenden Plan überschriebe der Vorschlag eine bewusste Abweichung.
   */
  const applyCategory = (value: string) => {
    set('maintenanceCategoryId', value);
    if (plan) return;
    const category = maintenanceCategories.find((entry: any) => String(entry.id) === value);
    if (!category) return;
    setForm((current) => ({
      ...current,
      maintenanceCategoryId: value,
      intervalUnit: category.default_interval_unit,
      intervalValue: String(category.default_interval_value),
      leadDays: String(category.default_lead_days),
    }));
  };

  return (
    <Modal
      title={plan ? `Wartungsplan bearbeiten – ${plan.name}` : 'Wartungsplan anlegen'}
      onClose={onClose}
      size="wide"
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={form.name.trim().length < 2}
            onClick={async () => {
              try {
                const payload = {
                  name: form.name,
                  description: form.description || null,
                  propertyId: form.propertyId ? Number(form.propertyId) : null,
                  categoryId: form.categoryId ? Number(form.categoryId) : null,
                  maintenanceCategoryId: form.maintenanceCategoryId ? Number(form.maintenanceCategoryId) : null,
                  criticalityId: form.criticalityId ? Number(form.criticalityId) : null,
                  assigneeId: form.assigneeId ? Number(form.assigneeId) : null,
                  checklistTemplateId: form.checklistTemplateId ? Number(form.checklistTemplateId) : null,
                  intervalUnit: form.intervalUnit,
                  intervalValue: Number(form.intervalValue),
                  leadDays: Number(form.leadDays),
                  estimatedMinutes: Number(form.estimatedMinutes),
                  nextDueDate: form.nextDueDate,
                  isActive: form.isActive,
                };
                if (plan) await api.patch(`/api/maintenance/${plan.id}`, payload);
                else await api.post('/api/maintenance', payload);
                onSaved();
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <Field label="Bezeichnung" required>
          <input value={form.name} onChange={(event) => set('name', event.target.value)} autoFocus />
        </Field>
        <Field label="Beschreibung">
          <textarea value={form.description} onChange={(event) => set('description', event.target.value)} />
        </Field>
        <div className="form-grid">
          <Field
            label="Wartungskategorie"
            hint={plan ? undefined : 'Übernimmt Intervall und Vorlauf der Kategorie.'}
          >
            <SearchSelect
              value={String(form.maintenanceCategoryId ?? '')}
              onChange={(wert) => applyCategory(wert)}
              options={(maintenanceCategories ?? []).map((category: any) => ({ value: String(category.id), label: String(category.name) }))}
              placeholder="Ohne Kategorie"
            />
          </Field>
          <Field label="Objekt">
            <SearchSelect
              value={String(form.propertyId ?? '')}
              onChange={(wert) => set('propertyId', wert)}
              options={(properties.data ?? []).map((property: any) => ({ value: String(property.id), label: String(property.name) }))}
            />
          </Field>
          <Field label="Gewerk" hint="Wer arbeitet – im Unterschied zur Kategorie, die sagt, was geprüft wird.">
            <SearchSelect
              value={String(form.categoryId ?? '')}
              onChange={(wert) => set('categoryId', wert)}
              options={(catalogs.data?.categories ?? []).map((category: any) => ({ value: String(category.id), label: String(category.name) }))}
            />
          </Field>
          <Field label="Kritikalität">
            <SearchSelect
              value={String(form.criticalityId ?? '')}
              onChange={(wert) => set('criticalityId', wert)}
              options={(catalogs.data?.criticalityLevels ?? []).map((level: any) => ({ value: String(level.id), label: String(level.name) }))}
            />
          </Field>
          <Field label="Zuständig">
            <SearchSelect
              value={String(form.assigneeId ?? '')}
              onChange={(wert) => set('assigneeId', wert)}
              options={(users.data?.items ?? users.data ?? []).map((person: any) => ({ value: String(person.id), label: `${person.first_name} ${person.last_name}` }))}
              placeholder="Offen lassen"
            />
          </Field>
          <Field label="Checkliste">
            <SearchSelect
              value={String(form.checklistTemplateId ?? '')}
              onChange={(wert) => set('checklistTemplateId', wert)}
              options={(catalogs.data?.checklistTemplates ?? []).map((template: any) => ({ value: String(template.id), label: String(template.name) }))}
              placeholder="Keine"
            />
          </Field>
          <Field label="Intervall">
            <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
              <input
                type="number"
                min="1"
                value={form.intervalValue}
                onChange={(event) => set('intervalValue', event.target.value)}
                style={{ width: 80 }}
              />
              <select value={form.intervalUnit} onChange={(event) => set('intervalUnit', event.target.value)}>
                <option value="day">Tage</option>
                <option value="week">Wochen</option>
                <option value="month">Monate</option>
                <option value="year">Jahre</option>
              </select>
            </div>
          </Field>
          <Field label="Vorlauf (Tage)" hint="So viele Tage vor Fälligkeit wird der Auftrag erzeugt.">
            <input type="number" min="0" value={form.leadDays} onChange={(event) => set('leadDays', event.target.value)} />
          </Field>
          <Field label="Geplante Dauer (Min.)">
            <input
              type="number"
              min="0"
              value={form.estimatedMinutes}
              onChange={(event) => set('estimatedMinutes', event.target.value)}
            />
          </Field>
          <Field label="Nächste Fälligkeit" required>
            <input type="date" value={form.nextDueDate} onChange={(event) => set('nextDueDate', event.target.value)} />
          </Field>
        </div>

        <label className="checkbox-row">
          <input type="checkbox" checked={form.isActive} onChange={(event) => set('isActive', event.target.checked)} />
          <span>
            Aktiv
            <span className="small subtle" style={{ display: 'block' }}>
              Ausgesetzte Pläne erzeugen keine Aufträge, bleiben aber als Nachweis erhalten.
            </span>
          </span>
        </label>
      </div>
    </Modal>
  );
}

/** Intervall in Tagen – nur zum Sortieren, nicht zum Rechnen. */
function intervalDays(plan: any): number {
  const factor: Record<string, number> = { day: 1, week: 7, month: 30, year: 365 };
  return (plan.interval_value ?? 1) * (factor[plan.interval_unit] ?? 30);
}

function intervalLabel(unit: string, value: number): string {
  const map: Record<string, [string, string]> = {
    day: ['Tag', 'Tage'],
    week: ['Woche', 'Wochen'],
    month: ['Monat', 'Monate'],
    year: ['Jahr', 'Jahre'],
  };
  const entry = map[unit] ?? [unit, unit];
  return value === 1 ? entry[0] : entry[1];
}
